import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import {
  Client,
  Person,
} from 'projects/brn-frontend/src/app/features/bot/modules/bot-crm/store';
import {
  ChangeDialogMode,
  ChatMessage,
  ChatMessageBody,
  ChatSelectors,
  GetMessages,
  GetSuggestions,
  PushNewMessage,
  SendMessage,
  SetClient,
  Suggestion,
  SwitchInfoMode,
} from '../../store';

import * as Faye from 'faye';
import { BotState } from 'projects/brn-frontend/src/app/core/store/bot/bot.state';
import { ChannelsPopupComponent } from '../../popups/channels-popup/channels-popup.component';
import {
  AbstractControl,
  FormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MediaPopupComponent } from '../../popups/media-popup/media-popup.component';
import { PortalService } from 'projects/brn-frontend/src/app/core/services/portal.service';

@Component({
  selector: 'brn-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  @Input()
  mobileView = false;

  @SelectSnapshot(BotState.getBotId)
  botId!: string;
  @ViewSelectSnapshot(ChatSelectors.client)
  client!: Client;
  @ViewSelectSnapshot(ChatSelectors.infoMode)
  infoMode!: boolean;
  @ViewSelectSnapshot(ChatSelectors.messages)
  messages!: ChatMessage[];
  @ViewSelectSnapshot(ChatSelectors.suggestions)
  suggestions!: Suggestion[];

  // Popups components
  public channelPopupComponent = ChannelsPopupComponent;
  public mediaPopupComponent = MediaPopupComponent;

  fullscreenMode = false;

  private faye: any = {
    channel: null,
    host: PortalService.FayeHost,
  };

  private PERSON!: Person;
  @Input()
  set person(person: Person) {
    this.PERSON = person;
    this.store.dispatch(new SetClient(this.lastActiveClient));
  }
  get person(): Person {
    return this.PERSON;
  }

  chatForm = this.fb.group({
    textMessage: ['', [Validators.required, this.emptyValidator()]],
  });

  constructor(private store: Store, private fb: FormBuilder) {}

  loadMoreMessages(e: Event): void {
    this.store.dispatch(new GetMessages());
  }

  ngOnInit(): void {
    this.initFayeChanel();
    this.chatForm.valueChanges.subscribe((val) => {
      const keyword = val.textMessage;
      this.store.dispatch(new GetSuggestions(keyword));
    });
  }

  ngOnDestroy(): void {
    this.stopFayeChanel();
  }

  changeDialogMode(): void {
    this.store.dispatch(new ChangeDialogMode());
  }

  switchInfoMode(): void {
    this.store.dispatch(new SwitchInfoMode());
  }

  enableFullscreen(): void {
    this.fullscreenMode = true;
  }
  disableFullscreen(): void {
    this.fullscreenMode = false;
  }

  // Faye
  initFayeChanel(): void {
    const client = new Faye.Client(this.faye.host, {
      timeout: 20,
      retry: 5,
    });

    this.faye.channel = client.subscribe(
      `/bot_${this.botId}`,
      (msg: ChatMessage) => {
        if (this.client.client_id === msg.client_id) {
          this.store.dispatch(new PushNewMessage(msg));
        }
      }
    );
  }

  stopFayeChanel(): void {
    this.faye.channel.cancel();
  }

  // Getters

  get lastActiveClient(): Client | any {
    const lastProdider = this.person.statistics.last_message.provider || null;
    const lastClient = this.person.clients.find((client) => {
      return client.provider === lastProdider;
    });
    const defaultCLient = this.person.clients.find((client) => {
      return client.default === true;
    });
    return lastClient || defaultCLient;
  }

  get clients(): Client[] {
    return this.person.clients;
  }

  get channelIcon(): string {
    return `assets/images/socials/icons/${this.client.provider}.png`;
  }

  get autoMode(): boolean {
    if (this.client) {
      return this.client.mode === 'auto';
    }
    return false;
  }

  get personAvatar(): string {
    if (this.person.profile.image) {
      return this.person.profile.image;
    }
    return 'https://api.brn.ai/img/default-client-avatar.png';
  }

  get online(): boolean {
    // 2 mins offset
    const offset = 2 * 60 * 1000;
    const now = Date.now();
    const last = Date.parse(this.person.statistics.last_sent);
    return now - last < offset;
  }

  onAvatarNotFound(event: any): void {
    if (event.target) {
      event.target.src = 'https://api.brn.ai/img/default-client-avatar.png';
    }
  }

  // Messaging

  sendMessage(): void {
    const msg: ChatMessageBody = {
      type: 'text',
      content: {
        text: this.chatForm.value.textMessage,
      },
    };
    this.chatForm.reset();
    this.store.dispatch(new SendMessage(msg));
  }

  sendSuggestion(suggestion: Suggestion): void {
    const msg: ChatMessageBody = {
      type: 'text',
      content: {
        text: suggestion.text,
      },
    };
    this.chatForm.reset();
    this.store.dispatch(new SendMessage(msg));
  }
  private emptyValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const valid = (control.value || '').trim().length !== 0;
      return valid ? null : { value: control.value };
    };
  }
}
